import React from 'react'
import { FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Input } from '@node-space/ui-components'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { BeneficiaryEntityType } from 'types/beneficiaries'
import { Contact } from 'types/contacts'
import { AccountHolderDetailsCopy } from './AccountHolderDetailsCopy'

type AccountHolderDetailsProps = {
  contact: Contact
  formValues: ContactFiatInstrumentFormData
  errors: FieldErrors<ContactFiatInstrumentFormData>
  register: UseFormRegister<ContactFiatInstrumentFormData>
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
}

export const AccountHolderDetails = ({
  contact,
  formValues,
  errors,
  register,
  setValue,
}: AccountHolderDetailsProps) => {
  const { t } = useTranslation()

  const handleInputChange =
    (field: keyof ContactFiatInstrumentFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(field, event.target.value, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }

  const isIndividual = formValues?.entityType === BeneficiaryEntityType.INDIVIDUAL
  const isBusiness = formValues?.entityType === BeneficiaryEntityType.BUSINESS

  if (!isIndividual && !isBusiness) {
    return null
  }

  return (
    <Box gapY={16} flexCol width="full">
      <Box flexCol width="full" paddingT={16} paddingB={8} gapY={16}>
        <h2 className="text-foreground-prominent text-h2">
          {t('formFields.beneficiaryDetails')}
        </h2>

        <AccountHolderDetailsCopy formValues={formValues} contact={contact} setValue={setValue} />
      </Box>

    {!formValues?.sameAccountHolderNameAsContact && (
      <Box flexCol width="full" gapY={16}>
        {isBusiness && (
          <Input
            {...register('businessName')}
            testId="businessName"
            label={t('businessName')}
            placeholder={t('enterBusinessName')}
            errorText={errors?.businessName?.message}
            error={!!errors?.businessName?.message}
            value={formValues?.businessName || ''}
            onChange={handleInputChange('businessName')}
          />
        )}

        {isIndividual && (
          <>
            <Input
              {...register('firstName')}
              testId="firstName"
              label={t('formFields.firstName')}
              placeholder={t('enterName')}
              errorText={errors?.firstName?.message}
              error={!!errors?.firstName?.message}
              value={formValues?.firstName || ''}
              onChange={handleInputChange('firstName')}
            />
            <Input
              {...register('lastName')}
              testId="lastName"
              label={t('formFields.lastName')}
              placeholder={t('enterLastName')}
              errorText={errors?.lastName?.message}
              error={!!errors?.lastName?.message}
              value={formValues?.lastName || ''}
              onChange={handleInputChange('lastName')}
            />
          </>
        )}
      </Box>
    )}
    </Box>
  )
}
